
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';
import { generateSchema, SchemaConfig } from '@/utils/schemaGenerator';

export const SEOHead = () => {
  const location = useLocation();
  
  const { data: siteSettings } = useQuery({
    queryKey: ['seo-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['google_search_console_tag', 'site_title', 'site_description']);
      
      if (error) throw error;
      
      const settingsMap: Record<string, string> = {};
      data?.forEach(setting => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      
      return settingsMap;
    }
  });

  // Fetch page-specific SEO data for dynamic pages
  const { data: pageData } = useQuery({
    queryKey: ['page-seo', location.pathname],
    queryFn: async () => {
      // Skip if it's not a dynamic page
      if (location.pathname === '/' || location.pathname.startsWith('/blog') || 
          location.pathname.startsWith('/admin') || location.pathname.startsWith('/auth')) {
        return null;
      }

      const slug = location.pathname.substring(1);
      
      const { data, error } = await supabase
        .from('pages')
        .select('meta_title, meta_description, canonical_url, custom_schema_json, title, robots_index, robots_follow')
        .eq('page_slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching page SEO data:', error);
        return null;
      }
      
      return data;
    },
    enabled: Boolean(location.pathname && location.pathname !== '/'),
  });

  // Fetch blog post data for blog pages
  const { data: blogData } = useQuery({
    queryKey: ['blog-seo', location.pathname],
    queryFn: async () => {
      if (!location.pathname.startsWith('/blog/')) {
        return null;
      }

      const slug = location.pathname.replace('/blog/', '');
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('title, meta_title, meta_description, author, publication_date, updated_at, featured_image_url, excerpt, canonical_url, custom_schema_json, robots_index, robots_follow')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();

      if (error) {
        console.error('Error fetching blog SEO data:', error);
        return null;
      }
      
      return data;
    },
    enabled: Boolean(location.pathname.startsWith('/blog/')),
  });

  // Set default SEO values
  const getDefaultTitle = () => {
    if (location.pathname === '/') {
      return "Orizis | AI AUTOMATION & Digital Marketing Agency";
    }

    // Extract page title from DOM for static service pages
    const h1Element = document.querySelector('h1');
    if (h1Element && location.pathname.includes('service')) {
      const h1Text = h1Element.textContent?.trim();
      if (h1Text && h1Text !== "UK's Fastest-Growing AI Automation Agency") {
        return `${h1Text} | Orizis`;
      }
    }
    
    return siteSettings?.site_title || "UK's Fastest-Growing AI Automation Agency | Orizis";
  };

  const getDefaultDescription = () => {
    if (location.pathname === '/') {
      return 'Orizis delivers custom AI-driven automation solutions for UK businesses—boosting productivity, reducing costs, and unlocking scalable growth without increasing headcount.';
    }
    
    // Extract description from first paragraph after h1 for static service pages
    if (location.pathname.includes('service')) {
      const h1Element = document.querySelector('h1');
      if (h1Element) {
        const nextParagraph = h1Element.closest('section')?.querySelector('p');
        if (nextParagraph) {
          const description = nextParagraph.textContent?.trim();
          if (description && description.length > 50) {
            return description.length > 160 ? description.substring(0, 157) + '...' : description;
          }
        }
      }
    }
    
    return siteSettings?.site_description || 'Orizis delivers custom AI-driven automation solutions for UK businesses—boosting productivity, reducing costs, and unlocking scalable growth without increasing headcount.';
  };

  const getCanonicalUrl = () => {
    const baseUrl = 'https://www.orizis.co.uk';
    
    // If page has a custom canonical URL in the CMS, use that as highest priority
    if (pageData?.canonical_url && pageData.canonical_url.trim()) {
      return pageData.canonical_url;
    }
    
    if (blogData?.canonical_url && blogData.canonical_url.trim()) {
      return blogData.canonical_url;
    }
    
    // Generate canonical URL with proper domain and clean path
    let cleanPath = location.pathname;
    
    // Remove trailing slash except for root
    if (cleanPath !== '/' && cleanPath.endsWith('/')) {
      cleanPath = cleanPath.slice(0, -1);
    }
    
    // Always use HTTPS and www for canonical URLs to prevent duplicate content
    return baseUrl + cleanPath;
  };

  // Determine page type for schema generation
  const getPageType = (): SchemaConfig['pageType'] => {
    if (location.pathname === '/') return 'homepage';
    if (location.pathname === '/blog') return 'blog-listing';
    if (location.pathname.startsWith('/blog/')) return 'blog-post';
    if (location.pathname.includes('contact')) return 'contact';
    if (location.pathname.includes('about')) return 'about';
    if (location.pathname.includes('service')) return 'service';
    return 'general';
  };

  // Generate structured data
  const generateStructuredData = () => {
    const pageType = getPageType();
    const currentUrl = getCanonicalUrl();
    
    let title = pageData?.meta_title || blogData?.meta_title || getDefaultTitle();
    let description = pageData?.meta_description || blogData?.meta_description || getDefaultDescription();
    
    // For service pages, extract the clean service name from the h1
    if (pageType === 'service') {
      const h1Element = document.querySelector('h1');
      if (h1Element) {
        const h1Text = h1Element.textContent?.trim() || '';
        // Remove "| Orizis" suffix if it exists
        title = h1Text.replace(/\s*\|\s*Orizis\s*$/, '');
      }
    }
    
    const schemaConfig: SchemaConfig = {
      pageType,
      url: currentUrl,
      title,
      description,
    };

    // Add blog-specific data
    if (blogData && pageType === 'blog-post') {
      schemaConfig.author = blogData.author;
      schemaConfig.publishedDate = blogData.publication_date;
      schemaConfig.modifiedDate = blogData.updated_at;
      schemaConfig.featuredImage = blogData.featured_image_url;
      schemaConfig.excerpt = blogData.excerpt;
    }

    return generateSchema(schemaConfig);
  };

  useEffect(() => {
    if (siteSettings) {
      // Add Google Search Console verification
      if (siteSettings.google_search_console_tag) {
        let existingTag = document.querySelector('meta[name="google-site-verification"]');
        if (existingTag) {
          existingTag.remove();
        }
        
        const metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'google-site-verification');
        metaTag.setAttribute('content', siteSettings.google_search_console_tag);
        document.head.appendChild(metaTag);
      }
    }
  }, [siteSettings, pageData]);

  // Generate robots meta tag based on page settings
  const generateRobotsContent = () => {
    // Default to index,follow if no specific settings
    let index = 'index';
    let follow = 'follow';
    
    // Check page data first
    if (pageData) {
      index = pageData.robots_index !== false ? 'index' : 'noindex';
      follow = pageData.robots_follow !== false ? 'follow' : 'nofollow';
    }
    // Then check blog data
    else if (blogData) {
      index = blogData.robots_index !== false ? 'index' : 'noindex';
      follow = blogData.robots_follow !== false ? 'follow' : 'nofollow';
    }
    
    return `${index},${follow}`;
  };

  // Parse and validate custom schema JSON
  const getCustomSchema = () => {
    const customSchemaJson = pageData?.custom_schema_json || blogData?.custom_schema_json;
    if (!customSchemaJson) return null;
    
    try {
      return JSON.parse(customSchemaJson);
    } catch (error) {
      console.warn('Invalid custom schema JSON:', error);
      return null;
    }
  };

  return (
    <Helmet>
      {/* Page title */}
      <title>{pageData?.meta_title || blogData?.meta_title || getDefaultTitle()}</title>
      
      {/* Meta description */}
      <meta name="description" content={pageData?.meta_description || blogData?.meta_description || getDefaultDescription()} />
      
      {/* Canonical URL - always points to clean URL without parameters */}
      <link rel="canonical" href={getCanonicalUrl()} />
      
      {/* Robots meta tag */}
      <meta name="robots" content={generateRobotsContent()} />
      
      {/* Auto-generated Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData())}
      </script>
      
      {/* Custom Schema Markup (if provided) */}
      {getCustomSchema() && (
        <script type="application/ld+json">
          {JSON.stringify(getCustomSchema())}
        </script>
      )}
      
      {/* Open Graph tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:title" content={pageData?.meta_title || blogData?.meta_title || getDefaultTitle()} />
      <meta property="og:description" content={pageData?.meta_description || blogData?.meta_description || getDefaultDescription()} />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageData?.meta_title || blogData?.meta_title || getDefaultTitle()} />
      <meta name="twitter:description" content={pageData?.meta_description || blogData?.meta_description || getDefaultDescription()} />
    </Helmet>
  );
};
